import * as React from 'react';
import SuperCritical from 'lib/supercritical';

import { PilotJobOrder } from 'data/models/PilotJobOrder';

import { MetroMapMilestone } from 'portcall/metromapframework/model';

import { AddPortCallPilotsTab as PilotsTab, PilotJobOrderViewModel } from './pilots';

import { FeetInchesMetersEditView } from 'portcall/feetinchesmetersedit/view';
import {
    MilestoneView,
    MilestoneHeaderRow,
    MilestoneScheduleDetailsRow,
    MovementTypeParticiple
} from 'portcall/metromapframework/view';
import { NumericInput } from 'portcall/numericinput/view';
import { PortCallScheduleHeaderView } from 'portcall/portcallschedule/view';
import { ToggleButton } from 'portcall/togglebutton/view';
import { NullableDateTimePicker } from 'portcall/datetimepicker/view';

export const AddPortCallPilotsTabView = SuperCritical(({ model: m } : { model : PilotsTab }) =>
    <div className="addportcallpilotstab">
        <PortCallScheduleHeaderView className="schedule-header" model={m.ScheduleHeader} />
        <RequiresPilotSelector model={m} />
        <PilotAgencySummary model={m} />
        <PilotJobOrdersView model={m} />
        <PilotageBilling model={m} />
        <Remarks model={m} />
        <Services model={m} />
    </div>
);

const RequiresPilotSelector = SuperCritical(({ model: m } : { model : PilotsTab }) =>
    <div className="requires-pilot">
        <span className="requires-pilot-label">Does this ship require a pilot?</span>
        <ToggleButton checked={m.IsPilotRequired} onLabel="Yes" offLabel="No" />
    </div>
);

const PilotAgencySummary = SuperCritical(({ model: m } : { model : PilotsTab }) => {
    const agency = m.Template()?.PilotAgency();
    return agency ?
        <div className="pilot-agency-summary">
            {agency.Organization()?.Name()}
            {' '}
            &mdash;
            {' '}
            Pilot Orders: Additional Information
        </div> :
        null;
});

// Higher than currently needed to leave room for adding more.
const TAB_STOPS_PER_PJO = 12;

const PilotJobOrdersView = SuperCritical(({ model: m } : { model : PilotsTab }) =>
    <div className="pilot-job-orders">
        {m.PilotJobOrders().map((pjo, i) =>
            <PilotJobOrderView
                pjo={pjo}
                // Add an extra PJO row's worth of tab stops in case we wanna have some before the first PJO.
                tabIndexStart={(i + 1) * TAB_STOPS_PER_PJO}
                key={PJOKey(pjo)}
                autoFocus={i == 0 ? true : false}
            />
        )}
    </div>
);

let nextPJOKey = 0;
const PJO_KEYS = new WeakMap<PilotJobOrderViewModel, number>();
function PJOKey(pjo : PilotJobOrderViewModel) {
    let id = PJO_KEYS.get(pjo);
    if (id === undefined) {
        id = nextPJOKey++;
        PJO_KEYS.set(pjo, id);
    }
    return id;
}

const PilotJobOrderView = SuperCritical(({ pjo, tabIndexStart, autoFocus } : {
    pjo : PilotJobOrderViewModel,
    tabIndexStart : number,
    autoFocus : boolean
}) =>
    <div className="pilot-job-order">
        <PilotJobOrderMilestone pjo={pjo} />
        <PilotJobOrderDrafts pjo={pjo} tabIndexStart={tabIndexStart} autoFocus={autoFocus} />
    </div>
);

const PilotJobOrderMilestone = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) => {
    const m = pjo.Milestone();
    return m ?
        <MilestoneView
            milestone={m}
            ctrl={{ milestoneContent: m => <PJOMilestoneContent milestone={m} /> }}
        /> : null;
});

const PJOMilestoneContent = SuperCritical(({ milestone: m } : { milestone : MetroMapMilestone }) =>
    <div>
        <MilestoneHeaderRow milestone={m} />
        <MilestoneScheduleDetailsRow milestone={m} />
    </div>
);

const PilotJobOrderDrafts = SuperCritical(({ pjo, tabIndexStart, autoFocus } : {
    pjo : PilotJobOrderViewModel,
    tabIndexStart : number,
    autoFocus : boolean
}) =>
    <div className="pilots-tab-section drafts">
        <div className="pilots-tab-section-header">
            Drafts - {pjo.Milestone() && MovementTypeParticiple(pjo.Milestone()!)}
        </div>
        <div className="pilots-tab-section-body">
            <div className="drafts-row">
                <span className="drafts-label pc-text-smalllabel">Deep Draft</span>
                {' '}
                <FeetInchesMetersEditView
                    model={pjo.DeepDraft}
                    tabindex={{ feet: tabIndexStart, inches: tabIndexStart + 1, meters: tabIndexStart + 2 }}
                    autoFocus={autoFocus}
                />
            </div>
            <div className="drafts-row">
                <span className="drafts-label pc-text-smalllabel">Air Draft</span>
                {' '}
                <FeetInchesMetersEditView
                    model={pjo.AirDraft}
                    tabindex={{ feet: tabIndexStart + 3, inches: tabIndexStart + 4, meters: tabIndexStart + 5 }}
                />
            </div>
        </div>
    </div>
);

function FirstTabStopAfterPJOs(m : PilotsTab) {
    // Add an extra PJO row's worth of tab stops in case we wanna have some before the first PJO.
    return (m.PilotJobOrders().length + 1) * TAB_STOPS_PER_PJO;
}

// Higher than currently needed to leave room for adding more.
const
    BILL_TO_AGENT_TAB_STOPS = 4,
    BILLING_TAB_STOPS = BILL_TO_AGENT_TAB_STOPS + 4;

const PilotageBilling = SuperCritical(({ model: m } : { model : PilotsTab }) => {
    const tmpl = m.Template();
    if (!tmpl) return null;

    const tabIndexStart = FirstTabStopAfterPJOs(m);
    return (
        <div className="pilots-tab-section billing-section">
            <div className="pilots-tab-section-header">Pilotage Billing</div>
            <div className="pilots-tab-section-body">
                <BillToAgentSelector tmpl={tmpl} tabIndexStart={tabIndexStart} />
                <BillingAddress tmpl={tmpl} tabIndex={tabIndexStart + BILL_TO_AGENT_TAB_STOPS} />
            </div>
        </div>
    );
});

const ENTER_KEY = 'Enter';

const BillToAgentSelector = SuperCritical(({ tmpl, tabIndexStart } : {
    tmpl : PilotJobOrder,
    tabIndexStart : number
}) =>
    <div className="bill-to-agent">
        <div className="pc-text-smalllabel">Send invoice to:</div>
        <div className="bill-to-option-shell">
            <label
                className="pc-option"
                tabIndex={tabIndexStart}
                // It seems there's no default keybinding for "click this focused label," at least not on Chrome.
                onKeyDown={e => e.key === ENTER_KEY && e.currentTarget.click()}
            >
                <input
                    className="pc-breaker-input"
                    type="radio"
                    checked={tmpl.BillToAgent()}
                    onChange={e => tmpl.BillToAgent(e.currentTarget.checked)}
                />
                <span className="pc-breaker-light" />
                <span className="pc-option-label">Agent</span>
            </label>
        </div>
        <div className="bill-to-option-shell">
            <label
                className="pc-option"
                tabIndex={tabIndexStart + 1}
                onKeyDown={e => e.key === ENTER_KEY && e.currentTarget.click()}
            >
                <input
                    className="pc-breaker-input"
                    type="radio"
                    checked={!tmpl.BillToAgent()}
                    onChange={e => tmpl.BillToAgent(!e.currentTarget.checked)}
                />
                <span className="pc-breaker-light" />
                <span className="pc-option-label">Other</span>
            </label>
        </div>
    </div>
);

const BillingAddress = SuperCritical(({ tmpl, tabIndex } : { tmpl : PilotJobOrder, tabIndex : number }) =>
    <div className="bill-to-address-shell">
        <div className="bill-to-address-label pc-text-smalllabel">Billing Address</div>
        <div>
            <textarea
                className="pilots-textarea"
                rows={4}
                value={tmpl.BillToAddress() ?? ''}
                onChange={e => tmpl.BillToAddress(e.currentTarget.value || null)}
                // Auto-select on focus to make it easy to override the default billing address if desired
                onFocus={e => e.currentTarget.select()}
                tabIndex={tabIndex}
            />
        </div>
    </div>
);

// Higher than currently needed to leave room for adding more.
const REMARKS_TAB_STOPS = 4;

const Remarks = SuperCritical(({ model: m } : { model : PilotsTab }) => {
    const tmpl = m.Template();
    return tmpl ?
        <div className="pilots-tab-section remarks-section">
            <div className="pilots-tab-section-header">Remarks</div>
            <div className="pilots-tab-section-body">
                <textarea
                    className="pilots-textarea"
                    rows={3}
                    placeholder="Enter remarks..."
                    value={tmpl.Remarks() ?? ''}
                    onChange={e => tmpl.Remarks(e.currentTarget.value || null)}
                    tabIndex={FirstTabStopAfterPJOs(m) + BILLING_TAB_STOPS}
                />
            </div>
        </div> :
        null;
});

const Services = SuperCritical(({ model: m } : { model : PilotsTab }) => {
    const tmpl = m.Template();
    if (!tmpl) return null;

    const tabIndexStart = FirstTabStopAfterPJOs(m) + BILLING_TAB_STOPS + REMARKS_TAB_STOPS;
    return (
        <div className="pilots-tab-section services-section">
            <div className="pilots-tab-section-header">Services</div>
            <div className="pilots-tab-section-body">
                <div className="services-row">
                    <span className="services-label pc-text-smalllabel">Tug</span>
                    <span>
                        <select className="pc-select service-main-select pjo-select"
                            value={tmpl.TugCompanyId() === null ? '' : '' + tmpl.TugCompanyId()}
                            onChange={e => tmpl.TugCompanyId(e.currentTarget.value === '' ? null : +e.currentTarget.value)}
                            tabIndex={tabIndexStart}
                        >
                            <option value="" key="">--</option>
                            {m.TugCompanies().map(tug =>
                                <option value={tug.Id()} key={tug.Id()}>{tug.Name()}</option>
                            )}
                        </select>
                        <span className="pc-select-chevron"></span>
                    </span>
                </div>
                <div className="services-row">
                    <span className="services-label pc-text-smalllabel">Tug #</span>
                    <NumericInput
                        className="pilots-input tugs-input"
                        value={tmpl.NumberOfTugs}
                        tabindex={tabIndexStart + 1}
                    />
                </div>
                <div className="services-row">
                    <span className="services-label pc-text-smalllabel">Line</span>
                    <span>
                        <select className="pc-select service-main-select pjo-select"
                            value={tmpl.LineHandlingCompanyId() === null ? '' : '' + tmpl.LineHandlingCompanyId()}
                            onChange={e => tmpl.LineHandlingCompanyId(e.currentTarget.value === '' ? null : +e.currentTarget.value)}
                            tabIndex={tabIndexStart + 2}
                        >
                            <option value="" key="">--</option>
                            {m.LineHandlers().map(line =>
                                <option value={line.Id()} key={line.Id()}>{line.Name()}</option>
                            )}
                        </select>
                        <span className="pc-select-chevron"></span>
                    </span>
                </div>
                <div className="services-row">
                    <span className="services-label pc-text-smalllabel">Labor</span>
                    <NullableDateTimePicker
                        value={tmpl.LaborStartTime}
                        localization={m.Localization}
                        tabindex={{ date: tabIndexStart + 3, time: tabIndexStart + 4 }}
                    />
                </div>
            </div>
        </div>
    );
});



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/pilotsview.tsx